import type { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import { GrPlan } from 'react-icons/gr';

import { type GetMicrostoreSitesPaginatedQuery, SiteState, SiteType } from 'data/__generated__';
import { multiSelectColumn, numberColumn, textColumn } from 'components/DataTable';

import { TypeTag } from 'pages/Sites/components/TypeTag';
import { StatusTag } from 'pages/Sites/components/StatusTag';
import { DetailLink } from 'components/DetailsView';

export type McgSiteRow = GetMicrostoreSitesPaginatedQuery['microstoreSitesWithColumnGroupsFilter']['sites'][number];

export type McgSiteFilterInput = {
    filterString?: string;
    stateFilter?: SiteState[];
};

export const mcgSiteColumns: ColumnDef<McgSiteRow, unknown>[] = [
    textColumn<McgSiteRow, McgSiteFilterInput>({
        accessorKey: 'name',
        header: 'Nom',
        defaultVisible: true,
    }),
    textColumn<McgSiteRow, McgSiteFilterInput>({
        accessorKey: 'organizationName',
        header: 'Organisation',
        defaultVisible: true,
    }),
    numberColumn<McgSiteRow, McgSiteFilterInput>({
        id: 'numberOfColumnGroup',
        accessorFn: (row) => row.microstoreColumnGroups.length,
        header: 'Nombre de groupes',
        defaultVisible: false,
    }),
    numberColumn<McgSiteRow, McgSiteFilterInput>({
        id: 'numberOfColumn',
        accessorFn: (row) => row.microstoreColumnGroups.flatMap(({ columns }) => columns).length,
        header: 'Nombre de colonnes',
        defaultVisible: false,
    }),
    {
        id: 'namesOfColumn',
        header: 'Noms de colonnes',
        meta: { defaultVisible: true },
        enableSorting: false,
        cell: ({ row }) => {
            const flattenedColumns = row.original.microstoreColumnGroups.flatMap(({ columns }) => columns);
            return (
                <ul className="m-0 list-none p-0">
                    {flattenedColumns.map((column) => (
                        <li key={column._id}>
                            <DetailLink path="microstore-column" name={'• ' + column.name} value={column._id} />
                        </li>
                    ))}
                </ul>
            );
        },
    } as ColumnDef<McgSiteRow, unknown>,
    multiSelectColumn<McgSiteRow, McgSiteFilterInput>({
        accessorKey: 'type',
        header: 'Type',
        defaultVisible: true,
        options: Object.values(SiteType).map((type) => ({ value: type, label: type })),
        renderOption: (option) => <TypeTag type={option.value as SiteType} />,
        cell: ({ row }) => <TypeTag type={row.original.type} />,
    }),
    multiSelectColumn<McgSiteRow, McgSiteFilterInput>({
        accessorKey: 'state',
        header: 'Statut',
        filterKey: 'stateFilter',
        defaultVisible: true,
        options: Object.values(SiteState).map((state) => ({ value: state, label: state })),
        renderOption: (option) => <StatusTag state={option.value as SiteState} />,
        cell: ({ row }) => <StatusTag state={row.original.state} />,
    }),
    {
        id: 'planogramLink',
        header: 'Planogramme',
        enableSorting: false,
        meta: { defaultVisible: true },
        cell: ({ row }) => (
            <Link to={`/planogram/${row.original._id}`} onClick={(e) => e.stopPropagation()}>
                <GrPlan />
            </Link>
        ),
    } as ColumnDef<McgSiteRow, unknown>,
];
